import { H6 } from "@sellar-io/components";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useMediaQuery } from "usehooks-ts";
import { useShallow } from "zustand/react/shallow";
import { Icon } from "../../../../components";
import {
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";

import { DiscoveryFilterButton } from "../components/DiscoveryFilterButton";
import { FilterDrawerHeader } from "../components/DiscoveryMobileFilterDrawer";
import { useDiscoveryV2SearchStore } from "../DiscoveryV2SearchStore";
import { CollapsableFilterCard } from "./FilterCard";
import { beerVarieties, spiritVarieties } from "@sellar-io/common";
import { filterStyles } from "~/components/marketSearch/marketSearchFilters";

type FilterStyleChild = {
  searchValue: string;
  displayValue: string;
};

export type FilterStyle<StyleKey extends string = string> = {
  [key in StyleKey]: {
    key: StyleKey;
    searchValue: string | undefined;
    displayValue: string;
    children?: FilterStyleChild[];
  };
};

export type FilterCategoryKey = keyof typeof filterStyles;

export const FilterStyleCard = ({ onClick }: { onClick?: () => void }) => {
  const isDesktop = useMediaQuery("(min-width: 1084px)");
  const {
    setStyleSearchValues,
    styleSearchValues,
    categoryKey,
    setCategoryKey,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      setStyleSearchValues: state.setStyleSearchValues,
      styleSearchValues: state.styleSearchValues,
      categoryKey: state.categoryKey,
      setCategoryKey: state.setCategoryKey,
    })),
  );

  const [styleFilters, setStyleFilters] = useState<string[]>(styleSearchValues);
  const [previousStyleSearchValues, setPreviousStyleSearchValues] =
    useState(styleSearchValues);

  if (styleSearchValues !== previousStyleSearchValues) {
    setPreviousStyleSearchValues(styleSearchValues);
    setStyleFilters(styleSearchValues);
  }

  const updateStyleFilters: Dispatch<SetStateAction<string[]>> = (updater) => {
    const updated =
      typeof updater === "function" ? updater(styleFilters) : updater;

    setStyleFilters(updated);
    setStyleSearchValues(
      updated.length === 1 && updated[0] === "" ? [] : updated,
    );
  };

  useEffect(() => {
    if (styleSearchValues && styleSearchValues.length >= 1) {
      const categoryFromSearchValue = Object.values(filterStyles).find(
        (category) => category.searchValue === styleSearchValues[0],
      );

      if (categoryFromSearchValue && styleSearchValues.length === 1) {
        setCategoryKey(categoryFromSearchValue.key);
        return setStyleSearchValues([]);
      }

      let categoryKey: FilterCategoryKey;
      if (
        beerVarieties.some((beer) =>
          styleSearchValues[0]?.toLowerCase().includes(beer),
        )
      ) {
        categoryKey = "beer";
      } else if (styleSearchValues[0]?.toLowerCase().includes("cider")) {
        categoryKey = "cider";
      } else if (
        spiritVarieties.some((spirit) =>
          styleSearchValues[0]?.toLowerCase().includes(spirit),
        )
      ) {
        categoryKey = "spirits";
      } else if (styleSearchValues[0]?.toLowerCase().includes("wine")) {
        categoryKey = "wine";
      } else {
        categoryKey = "softDrinks";
      }

      return setCategoryKey(categoryKey);
    }
  }, [
    setCategoryKey,
    styleSearchValues,
    styleSearchValues.length,
    setStyleSearchValues,
  ]);

  const category = categoryKey ? filterStyles[categoryKey] : undefined;

  return (
    <>
      {isDesktop ? (
        <FilterStyleCardDesktop
          styleFilters={styleFilters}
          setStyleFilters={updateStyleFilters}
          category={category}
          setCategoryKey={setCategoryKey}
          resetStyleFilter={() => {
            setCategoryKey(undefined);
            setStyleFilters([]);
            setStyleSearchValues([]);
          }}
        />
      ) : (
        <FilterStyleCardMobile
          styleFilters={styleFilters}
          onClick={onClick}
          setStyleFilters={updateStyleFilters}
          category={category}
          setCategoryKey={setCategoryKey}
          categoryKey={categoryKey}
          resetStyleFilter={() => {
            setCategoryKey(undefined);
            setStyleFilters([]);
            setStyleSearchValues([]);
          }}
        />
      )}
    </>
  );
};

const FilterStyleCardMobile = ({
  setStyleFilters,
  styleFilters,
  category,
  setCategoryKey,
  onClick,
  resetStyleFilter,
  categoryKey,
}: {
  setStyleFilters: Dispatch<SetStateAction<string[]>>;
  styleFilters: string[];
  setCategoryKey: (key: FilterCategoryKey | undefined) => void;
  categoryKey: FilterCategoryKey | undefined;
  category: FilterStyle["key"] | undefined;
  onClick?: () => void;
  resetStyleFilter: () => void;
}) => {
  const generateSearchValues = (
    category: FilterStyle["key"],
    categoryChild: FilterStyleChild,
  ) => {
    return ["beer", "spirits", "softDrinks"].includes(category.key)
      ? categoryChild.searchValue
      : [categoryChild.searchValue, category.searchValue].join(" & ");
  };

  return (
    <div className={"flex flex-col"}>
      <FilterDrawerHeader title={"Style"} onClick={onClick} />
      <div className={"p-4"}>
        <InputContainer>
          <InputSelect
            value={categoryKey}
            onChange={(e) => {
              resetStyleFilter();
              if (e.target.value !== "all") {
                setCategoryKey(e.currentTarget.value as FilterCategoryKey);
              }
            }}
          >
            <InputOption value={"all"}>All</InputOption>
            {Object.values(filterStyles).map((category) => (
              <InputOption key={category.key} value={category.key}>
                {category.displayValue}
              </InputOption>
            ))}
          </InputSelect>
        </InputContainer>
        {category && (
          <div className={"mt-4 flex flex-col gap-1"}>
            {category.children?.map((categoryChild) => (
              <DiscoveryFilterButton
                key={categoryChild.displayValue}
                title={categoryChild.displayValue}
                selected={styleFilters.includes(
                  generateSearchValues(category, categoryChild),
                )}
                onClick={() => {
                  const searchValue = generateSearchValues(
                    category,
                    categoryChild,
                  );
                  if (styleFilters.includes(searchValue)) {
                    const updateFilterValue = styleFilters.filter(
                      (value) => value !== searchValue,
                    );

                    if (updateFilterValue.length || category.searchValue) {
                      return setStyleFilters(
                        updateFilterValue.length
                          ? updateFilterValue
                          : category.searchValue
                            ? [category.searchValue]
                            : [],
                      );
                    }

                    return resetStyleFilter();
                  }
                  return setStyleFilters((styleFilters) => [
                    ...styleFilters,
                    searchValue,
                  ]);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const FilterStyleCardDesktop = ({
  setStyleFilters,
  styleFilters,
  category,
  setCategoryKey,
  resetStyleFilter,
}: {
  setStyleFilters: Dispatch<SetStateAction<string[]>>;
  styleFilters: string[];
  setCategoryKey: (key: FilterCategoryKey | undefined) => void;
  category: FilterStyle["key"] | undefined;
  resetStyleFilter: () => void;
}) => {
  const generateSearchValues = (
    category: FilterStyle["key"],
    categoryChild: FilterStyleChild,
  ) => {
    return ["beer", "spirits", "softDrinks"].includes(category.key)
      ? categoryChild.searchValue
      : [categoryChild.searchValue, category.searchValue].join(" & ");
  };

  return (
    <CollapsableFilterCard
      header={
        <div className={"flex items-center"}>
          <H6 className={twMerge("cursor-pointer")} onClick={resetStyleFilter}>
            Style
          </H6>
          {category ? (
            <>
              <Icon icon={"CHEVRON_RIGHT"} />
              {category.displayValue}
            </>
          ) : null}
        </div>
      }
    >
      {category ? (
        category.children?.map((categoryChild) => (
          <DiscoveryFilterButton
            key={categoryChild.displayValue}
            selected={styleFilters.includes(
              generateSearchValues(category, categoryChild),
            )}
            title={categoryChild.displayValue}
            onClick={() => {
              console.log("clicked");
              const searchValue = generateSearchValues(category, categoryChild);
              if (styleFilters.includes(searchValue)) {
                return setStyleFilters((styleFilters) =>
                  styleFilters.filter((value) => value !== searchValue),
                );
              }
              return setStyleFilters((styleFilters) => [
                ...styleFilters,
                searchValue,
              ]);
            }}
          />
        ))
      ) : (
        <>
          {Object.values(filterStyles).map((category) => (
            <DiscoveryFilterButton
              key={category.key}
              onClick={() => setCategoryKey(category.key)}
              title={category.displayValue}
            />
          ))}
        </>
      )}
    </CollapsableFilterCard>
  );
};
